import { useState, type FormEvent } from 'react';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.requestMagicLink(email.trim());
      setSent(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Something went wrong. Try again in a moment.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-4">
      <Card className="w-full max-w-md p-8">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent text-text-white font-bold">
            ci
          </div>
          <div>
            <h1 className="text-lg font-semibold text-text-primary">partnership-iq</h1>
            <p className="text-xs text-text-muted">Influencer & affiliate platform</p>
          </div>
        </div>

        {sent ? (
          <div className="space-y-3">
            <h2 className="text-base font-semibold text-text-primary">Check your email</h2>
            <p className="text-sm text-text-secondary">
              We sent a sign-in link to <strong>{email}</strong>. The link expires in 15 minutes.
            </p>
            <Button
              variant="secondary"
              onClick={() => {
                setSent(false);
                setEmail('');
              }}
            >
              Use a different email
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <h2 className="mb-1 text-base font-semibold text-text-primary">Sign in</h2>
              <p className="text-sm text-text-muted">
                Continue with Google or get a one-time email link.
              </p>
            </div>
            <a
              href="/api/auth/google/start"
              className="flex h-10 w-full items-center justify-center gap-2 rounded-md border border-border-subtle bg-bg-card text-sm font-medium text-text-primary hover:bg-bg-hover"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 48 48"
                aria-hidden="true"
                focusable="false"
              >
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16.1 18.9 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 44c5.3 0 10.1-2 13.8-5.3l-6.4-5.4C29.4 34.6 26.8 35.5 24 35.5c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.6l6.4 5.4C42.2 35.6 44 30.2 44 24c0-1.3-.1-2.4-.4-3.5z"/>
              </svg>
              Continue with Google
            </a>
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-border-subtle" />
              <span className="text-xs text-text-muted">or</span>
              <div className="h-px flex-1 bg-border-subtle" />
            </div>
            <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="mb-1 block text-xs font-medium text-text-secondary">
                Work email
              </label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                placeholder="you@brand.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            {error && (
              <div className="rounded-md border border-status-declining-bg bg-status-declining-bg/50 px-3 py-2 text-xs text-status-declining">
                {error}
              </div>
            )}
            {/* Primary CTA: h-10 matches the Google button height so the two
                stack visually, size=lg + font-semibold + brand accent makes
                this the clear action focus. Pre-redesign was h-9 medium-weight
                — Gemini flagged the lower visual weight as muted/branding-light. */}
            <Button
              type="submit"
              size="lg"
              disabled={submitting || !email}
              className="w-full font-semibold"
            >
              {submitting ? 'Sending…' : 'Send magic link'}
            </Button>
            </form>
          </div>
        )}
      </Card>
    </div>
  );
}
